import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Trash2, Eye, Pencil } from "lucide-react";
import { useAuthStore } from "@/lib/auth";
import { useMedicationTemplatesStore } from "@/lib/stores/medicationTemplates";
import { useAdditionalNotesTemplatesStore, deleteAdditionalNotesTemplate } from "@/lib/stores/additionalNotesTemplates";
import { getDoctorIdForUser, ensureDoctorProfile } from "@/lib/utils/doctorUtils";
import { SaveTemplateDialog } from "./SaveTemplateDialog";
import { SaveAdditionalNotesTemplateDialog } from "./SaveAdditionalNotesTemplateDialog";

interface TemplatesManagerDialogProps {
	isOpen: boolean;
	onClose: () => void;
}

export function TemplatesManagerDialog({ isOpen, onClose }: TemplatesManagerDialogProps) {
	const { user } = useAuthStore();
	const {
		templates: medicationTemplates,
		fetchTemplates: fetchMedicationTemplates,
		removeTemplate: removeMedicationTemplate,
		searchTemplates: searchMedicationTemplates,
		clearTemplates: clearMedicationTemplates,
	} = useMedicationTemplatesStore();
	const {
		templates: notesTemplates,
		fetchTemplates: fetchNotesTemplates,
		removeTemplate: removeNotesTemplateLocal,
		searchTemplates: searchNotesTemplates,
		clearTemplates: clearNotesTemplates,
	} = useAdditionalNotesTemplatesStore();

	const [diagnosisSearch, setDiagnosisSearch] = useState("");
	const [notesSearch, setNotesSearch] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState("medications");
	const [showCreateMedication, setShowCreateMedication] = useState(false);
	const [showCreateNotes, setShowCreateNotes] = useState(false);

	const [viewTemplate, setViewTemplate] = useState<any | null>(null);
	const [editTemplate, setEditTemplate] = useState<any | null>(null);

	useEffect(() => {
		if (!isOpen || !user?.id) return;
		let cancelled = false;
		(async () => {
			setIsLoading(true);
			try {
				let doctorId = await getDoctorIdForUser(user);
				if (!doctorId) doctorId = await ensureDoctorProfile(user);
				if (!doctorId) return;
				await Promise.all([
					fetchMedicationTemplates(doctorId),
					fetchNotesTemplates(doctorId),
				]);
				if (cancelled) return;
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isOpen, user?.id, fetchMedicationTemplates, fetchNotesTemplates]);

	useEffect(() => {
		if (!isOpen) {
			clearMedicationTemplates();
			clearNotesTemplates();
		}
	}, [isOpen, clearMedicationTemplates, clearNotesTemplates]);

	const filteredMedicationTemplates = useMemo(() => {
		if (!user?.id) return [];
		if (!diagnosisSearch.trim()) return medicationTemplates;
		return searchMedicationTemplates(user.id, diagnosisSearch);
	}, [medicationTemplates, diagnosisSearch, searchMedicationTemplates, user?.id]);

	const filteredNotesTemplates = useMemo(() => {
		if (!user?.id) return [];
		if (!notesSearch.trim()) return notesTemplates;
		return searchNotesTemplates(user.id, notesSearch);
	}, [notesTemplates, notesSearch, searchNotesTemplates, user?.id]);

	const handleDeleteMedication = async (id: string) => {
		if (!confirm("Delete this template? This action cannot be undone.")) return;
		removeMedicationTemplate(id);
	};

	const handleDeleteNotes = async (id: string) => {
		if (!confirm("Delete this template? This action cannot be undone.")) return;
		try {
			await deleteAdditionalNotesTemplate(id);
			removeNotesTemplateLocal(id);
		} catch (e) {
			console.error(e);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>My Templates</DialogTitle>
					<DialogDescription>Manage your saved medication and additional notes templates.</DialogDescription>
				</DialogHeader>

				<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
					<TabsList>
						<TabsTrigger value="medications">Medication Templates</TabsTrigger>
						<TabsTrigger value="notes">Additional Notes</TabsTrigger>
					</TabsList>

					<TabsContent value="medications" className="pt-4">
						<div className="flex items-center justify-between gap-4 mb-4">
							<div className="flex-1 max-w-md">
								<Label htmlFor="med-search" className="text-sm font-medium">Search</Label>
								<Input id="med-search" value={diagnosisSearch} onChange={(e) => setDiagnosisSearch(e.target.value)} placeholder="Search by diagnosis, template name, or medication..." />
							</div>
							<Button onClick={() => setShowCreateMedication(true)}>
								<Plus className="h-4 w-4 mr-1" />
								Create New Template
							</Button>
						</div>

						{isLoading ? (
							<div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading templates...</div>
						) : (
							<div className="border rounded-md overflow-hidden">
								<Table>
										<TableHeader>
										<TableRow>
											<TableHead>Diagnosis</TableHead>
											<TableHead>Template Name</TableHead>
											<TableHead>Medications</TableHead>
												<TableHead className="w-[160px]">Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filteredMedicationTemplates.length === 0 ? (
											<TableRow>
												<TableCell colSpan={5} className="text-sm text-muted-foreground">No templates found</TableCell>
											</TableRow>
										) : filteredMedicationTemplates.map((t) => (
											<TableRow key={t.id}>
												<TableCell>{t.diagnosis}</TableCell>
												<TableCell>{t.templateName}</TableCell>
												<TableCell>{t.medications?.length || 0} med(s)</TableCell>
												<TableCell>
													<div className="flex items-center gap-1">
														<Button variant="ghost" size="icon" onClick={() => setViewTemplate(t)} title="View">
															<Eye className="h-4 w-4" />
														</Button>
														<Button variant="ghost" size="icon" onClick={() => setEditTemplate(t)} title="Edit">
															<Pencil className="h-4 w-4" />
														</Button>
														<Button variant="ghost" size="icon" onClick={() => handleDeleteMedication(t.id)} title="Delete">
															<Trash2 className="h-4 w-4" />
														</Button>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>

					<TabsContent value="notes" className="pt-4">
						<div className="flex items-center justify-between gap-4 mb-4">
							<div className="flex-1 max-w-md">
								<Label htmlFor="notes-search" className="text-sm font-medium">Search</Label>
								<Input id="notes-search" value={notesSearch} onChange={(e) => setNotesSearch(e.target.value)} placeholder="Search by disease, template name, or text..." />
							</div>
							<Button onClick={() => setShowCreateNotes(true)}>
								<Plus className="h-4 w-4 mr-1" />
								Create New Template
							</Button>
						</div>

						{isLoading ? (
							<div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading templates...</div>
						) : (
							<div className="border rounded-md overflow-hidden">
								<Table>
										<TableHeader>
										<TableRow>
											<TableHead>Disease</TableHead>
											<TableHead>Template Name</TableHead>
												<TableHead>Notes</TableHead>
												<TableHead className="w-[160px]">Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filteredNotesTemplates.length === 0 ? (
											<TableRow>
												<TableCell colSpan={5} className="text-sm text-muted-foreground">No templates found</TableCell>
											</TableRow>
										) : filteredNotesTemplates.map((t) => (
											<TableRow key={t.id}>
												<TableCell>{t.disease}</TableCell>
												<TableCell>{t.templateName}</TableCell>
												<TableCell className="max-w-[320px] truncate" title={t.notes}>{t.notes}</TableCell>
												<TableCell>
													<div className="flex items-center gap-1">
														<Button variant="ghost" size="icon" onClick={() => setViewTemplate({
															mode: 'notes',
															disease: t.disease,
															templateName: t.templateName,
															notes: t.notes,
														})} title="View">
															<Eye className="h-4 w-4" />
														</Button>
														<Button variant="ghost" size="icon" onClick={() => setEditTemplate({
															mode: 'notes',
															id: t.id,
															disease: t.disease,
															templateName: t.templateName,
															notes: t.notes,
														})} title="Edit">
															<Pencil className="h-4 w-4" />
														</Button>
														<Button variant="ghost" size="icon" onClick={() => handleDeleteNotes(t.id)} title="Delete">
															<Trash2 className="h-4 w-4" />
														</Button>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>
				</Tabs>

				<SaveTemplateDialog isOpen={showCreateMedication} onClose={() => setShowCreateMedication(false)} medications={[]} allowEdit onTemplateSaved={() => setShowCreateMedication(false)} />
				<SaveAdditionalNotesTemplateDialog isOpen={showCreateNotes} onClose={() => setShowCreateNotes(false)} notes="" onTemplateSaved={() => setShowCreateNotes(false)} />

				{/* View Template Dialog (Medication or Notes) */}
				{viewTemplate && viewTemplate.mode !== 'notes' && (
					<Dialog open={!!viewTemplate} onOpenChange={() => setViewTemplate(null)}>
						<DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
							<DialogHeader>
								<DialogTitle>Template Preview</DialogTitle>
								<DialogDescription>
									{viewTemplate.diagnosis} — {viewTemplate.templateName}
								</DialogDescription>
							</DialogHeader>
							<div className="border rounded-md p-4">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Medication</TableHead>
											<TableHead>Dosage</TableHead>
											<TableHead>Frequency</TableHead>
											<TableHead>Duration</TableHead>
											<TableHead>Instructions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{(viewTemplate.medications || []).map((m: any, idx: number) => (
											<TableRow key={idx}>
												<TableCell className="font-medium">{m.name}</TableCell>
												<TableCell>{m.dosage} {m.doseType || ''}</TableCell>
												<TableCell>{m.frequency || '-'}</TableCell>
												<TableCell>{m.duration || '-'}</TableCell>
												<TableCell className="max-w-[280px] truncate" title={m.instructions}>{m.instructions || '-'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<div className="flex justify-end pt-4">
								<Button variant="outline" onClick={() => setViewTemplate(null)}>Close</Button>
							</div>
						</DialogContent>
					</Dialog>
				)}

				{viewTemplate && viewTemplate.mode === 'notes' && (
					<Dialog open={!!viewTemplate} onOpenChange={() => setViewTemplate(null)}>
						<DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
							<DialogHeader>
								<DialogTitle>Notes Template Preview</DialogTitle>
								<DialogDescription>
									{viewTemplate.disease} — {viewTemplate.templateName}
								</DialogDescription>
							</DialogHeader>

							<div className="border rounded-md p-4 whitespace-pre-wrap text-sm">
								{viewTemplate.notes}
							</div>

							<div className="flex justify-end pt-4">
								<Button variant="outline" onClick={() => setViewTemplate(null)}>Close</Button>
							</div>
						</DialogContent>
					</Dialog>
				)}

				{/* Edit Medication Template Dialog */}
				{editTemplate && editTemplate.mode !== 'notes' && (
					<SaveTemplateDialog
						isOpen={!!editTemplate}
						onClose={() => setEditTemplate(null)}
						medications={editTemplate.medications || []}
						allowEdit
						initialDiagnosis={editTemplate.diagnosis}
						initialTemplateName={editTemplate.templateName}
						onTemplateSaved={() => setEditTemplate(null)}
					/>
				)}

				{/* Edit Notes Template Dialog */}
				{editTemplate && editTemplate.mode === 'notes' && (
					<SaveAdditionalNotesTemplateDialog
						isOpen={!!editTemplate}
						onClose={() => setEditTemplate(null)}
						notes={editTemplate.notes || ''}
						templateId={editTemplate.id}
						initialDisease={editTemplate.disease}
						initialTemplateName={editTemplate.templateName}
						onTemplateSaved={() => setEditTemplate(null)}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}
